// Help Content Index
// Content is stored in locale files for i18n support
// This file provides structure and metadata

export interface HelpSection {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: string;
  articles: HelpArticleConfig[];
}

export interface HelpArticleConfig {
  id: string;
  slug: string;
  titleKey: string;
  descriptionKey: string;
  contentKey: string;
  faqKeys?: string[]; // Array of FAQ key prefixes
  relatedArticles?: string[]; // Article slugs
}

export interface FAQ {
  question: string;
  answer: string;
}

// Help sections configuration
export const helpSections: HelpSection[] = [
  {
    id: 'getting-started',
    titleKey: 'help.sections.gettingStarted.title',
    descriptionKey: 'help.sections.gettingStarted.description',
    icon: 'rocket',
    articles: [
      {
        id: 'getting-started-overview',
        slug: 'getting-started',
        titleKey: 'help.articles.gettingStarted.title',
        descriptionKey: 'help.articles.gettingStarted.description',
        contentKey: 'help.articles.gettingStarted.content',
        faqKeys: ['help.articles.gettingStarted.faqs'],
        relatedArticles: ['dashboard', 'widget-setup', 'theming'],
      },
    ],
  },
  {
    id: 'dashboard',
    titleKey: 'help.sections.dashboard.title',
    descriptionKey: 'help.sections.dashboard.description',
    icon: 'layout-dashboard',
    articles: [
      {
        id: 'dashboard-overview',
        slug: 'dashboard',
        titleKey: 'help.articles.dashboard.title',
        descriptionKey: 'help.articles.dashboard.description',
        contentKey: 'help.articles.dashboard.content',
        faqKeys: ['help.articles.dashboard.faqs'],
        relatedArticles: ['conversations', 'getting-started'],
      },
    ],
  },
  {
    id: 'widget',
    titleKey: 'help.sections.widget.title',
    descriptionKey: 'help.sections.widget.description',
    icon: 'message-circle',
    articles: [
      {
        id: 'widget-setup-guide',
        slug: 'widget-setup',
        titleKey: 'help.articles.widgetSetup.title',
        descriptionKey: 'help.articles.widgetSetup.description',
        contentKey: 'help.articles.widgetSetup.content',
        faqKeys: ['help.articles.widgetSetup.faqs'],
        relatedArticles: ['getting-started', 'theming'],
      },
    ],
  },
  {
    id: 'theming',
    titleKey: 'help.sections.theming.title',
    descriptionKey: 'help.sections.theming.description',
    icon: 'palette',
    articles: [
      {
        id: 'theming-guide',
        slug: 'theming',
        titleKey: 'help.articles.theming.title',
        descriptionKey: 'help.articles.theming.description',
        contentKey: 'help.articles.theming.content',
        faqKeys: ['help.articles.theming.faqs'],
        relatedArticles: ['widget-setup', 'getting-started'],
      },
    ],
  },
  {
    id: 'orchestrations',
    titleKey: 'help.sections.orchestrations.title',
    descriptionKey: 'help.sections.orchestrations.description',
    icon: 'workflow',
    articles: [
      {
        id: 'orchestrations-guide',
        slug: 'orchestrations',
        titleKey: 'help.articles.orchestrations.title',
        descriptionKey: 'help.articles.orchestrations.description',
        contentKey: 'help.articles.orchestrations.content',
        faqKeys: ['help.articles.orchestrations.faqs'],
        relatedArticles: ['dashboard', 'conversations'],
      },
    ],
  },
  {
    id: 'conversations',
    titleKey: 'help.sections.conversations.title',
    descriptionKey: 'help.sections.conversations.description',
    icon: 'messages-square',
    articles: [
      {
        id: 'conversations-guide',
        slug: 'conversations',
        titleKey: 'help.articles.conversations.title',
        descriptionKey: 'help.articles.conversations.description',
        contentKey: 'help.articles.conversations.content',
        faqKeys: ['help.articles.conversations.faqs'],
        relatedArticles: ['dashboard', 'orchestrations'],
      },
    ],
  },
];

// Helper to find article config by slug
export function getArticleConfigBySlug(slug: string): HelpArticleConfig | undefined {
  for (const section of helpSections) {
    const article = section.articles.find(a => a.slug === slug);
    if (article) return article;
  }
  return undefined;
}

// Helper to get all article configs flat
export function getAllArticleConfigs(): HelpArticleConfig[] {
  return helpSections.flatMap(section => section.articles);
}
